import { DateTime } from "luxon";
import { useTypeSelectors } from "./useTypeSelectors";
import { monthShort, outOfServiceReasonConstants } from "constants/constants";
import { formatNumber } from "utils/formatNumbers";

interface ThreeStatsData {
  name: string;
  count: number;
  list: any[];
  haslist: boolean;
  modalName?: string;
}

interface DataType {
  routes: {
    current: { name: string; count: number }[];
    prev_list: {
      standard_large: number;
      standard_cube: number;
      standard_other: number;
      flex: number;
      cancelled: number;
      rejected: number;
      other: number;
      report__report_date?: string;
      report__week_number?: number;
      report__year_number?: number;
      report__report_date__month?: number;
      report__report_date__year?: number;
    }[];
  };
  deliveries: {
    total: number;
    secondary_value: number;
    three_stats: ThreeStatsData[];
    prev_list: {
      deliveries: number;
      report__report_date?: string;
      report__week_number?: number;
      report__year_number?: number;
      report__report_date__month?: number;
      report__report_date__year?: number;
    }[];
  };
  returns: {
    total: number;
    secondary_value: number;
    three_stats: ThreeStatsData[];
    bar_chart_data: { name: string; count: number }[];
    period_chart_data: {
      returned_uta: number;
      returned_utl: number;
      returned_nsl: number;
      returned_bc: number;
      returned_otp_failure: number;
      returned_missing: number;
      returned_missort: number;
      returned_damage: number;
      returned_fdd: number;
      returned_oc: number;
      returned_others: number;
      report__report_date?: string;
      report__week_number?: number;
      report__year_number?: number;
      report__report_date__month?: number;
      report__report_date__year?: number;
    }[];
  };
  rabbit: {
    total: number;
    secondary_value: number;
    third_value: number;
    three_stats: ThreeStatsData[];
  };
  dolly: {
    total: number;
    secondary_value: number;
    third_value: number;
    three_stats: ThreeStatsData[];
  };
  da: {
    total: number;
    secondary_value: number;
    three_stats: ThreeStatsData[];
    period_chart_data: {
      max_shift_count: number;
      max_active_count: number;
      max_inactive_count: number;
      max_offboarded_count: number;
      date?: string;
      week_number?: number;
      year_number?: number;
      date__month?: number;
      date__year?: number;
    }[];
  };
  fleet: {
    total: number;
    secondary_value: number;
    three_stats: ThreeStatsData[];
    period_chart_data: {
      max_fleet_count: number;
      max_active_count: number;
      max_inactive_count: number;
      max_offboarded_count: number;
      date?: string;
      week_number?: number;
      year_number?: number;
      date__month?: number;
      date__year?: number;
    }[];
  };
}

export const useDashboardStats = (data: DataType) => {
  const { dashboardSettings } = useTypeSelectors((state) => state.dashboard);
  const mockedRouteData: any = {
    tag: "routes",
    previewLabels: [
      {
        name: "Standard Large",
        count: returnRoleCount("Standard Large", data?.routes.current),
        color: "#0129FA",
      },
      {
        name: "Standard Cube",
        count: returnRoleCount("Standard Cube", data?.routes.current),
        color: "#011DA7",
      },
      {
        name: "Standard Other",
        count: returnRoleCount("Standard Other", data?.routes.current),
        color: "#627282",
      },
      {
        name: "Flex",
        count: returnRoleCount("Flex", data?.routes.current),
        color: "#7A90FF",
      },
      {
        name: "Cancelled",
        count: returnRoleCount("Cancelled", data?.routes.current),
        color: "#C42020",
      },
      {
        name: "Rejected",
        count: returnRoleCount("Rejected", data?.routes.current),
        color: "#C28400",
      },
      {
        name: "Other",
        count: returnRoleCount("Other", data?.routes.current),
        color: "#164578",
      },
    ],
    current: data?.routes.current,
    prev_list: data?.routes.prev_list.map((item) => ({
      standard_large: item.standard_large,
      standard_cube: item.standard_cube,
      standard_other: item.standard_other,
      flex: item.flex,
      cancelled: item.cancelled,
      rejected: item.rejected,
      other: item.other,
      label: returnDateLabel(
        dashboardSettings.filterType,
        item?.report__report_date,
        item?.report__week_number,
        item?.report__report_date__month,
        item?.report__report_date__year,
      ),
    })),
  };

  const mockedDeliveryData: any = {
    tag: "deliveries",
    title: "Deliveries",
    smallText: "From previous",
    smallValue: formatNumber(data?.deliveries.total - data?.deliveries.secondary_value),
    secondTitle:
      dashboardSettings.filterType !== "year"
        ? `In the last 7 ${dashboardSettings.filterType?.replace("date", "day")}s`
        : `In this year`,
    alertTheme: data?.deliveries.total - data?.deliveries.secondary_value < 0 ? "error" : "success",
    total: formatNumber(data?.deliveries.total),
    prev_list: data?.deliveries.prev_list.map((item) => ({
      deliveries: item.deliveries,
      label: returnDateLabel(
        dashboardSettings.filterType,
        item?.report__report_date,
        item?.report__week_number,
        item?.report__report_date__month,
        item?.report__report_date__year,
      ),
    })),
    modalOneName: "Deliveries Trends",
    modalOneBtn: "Deliveries Trends",
  };

  const mockedReturnsData: any = {
    tag: "returns",
    title: "Returns To Station",
    smallText: "Plan: 0.5%",
    smallValue: `${data?.returns.secondary_value}%`,
    secondTitle: "Top Return Reasons",
    alertTheme: data?.returns.secondary_value > 0.5 ? "error" : "success",
    total: formatNumber(data?.returns.total),
    threeStats: data?.returns.three_stats,
    bar_chart_data: data?.returns.bar_chart_data,
    period_chart_data: data?.returns.period_chart_data.map((item) => ({
      uta: item?.returned_uta,
      utl: item?.returned_utl,
      otp_failure: item?.returned_otp_failure,
      others: item?.returned_others,
      oc: item?.returned_oc,
      nsl: item?.returned_nsl,
      missort: item?.returned_missort,
      missing: item?.returned_missing,
      fdd: item?.returned_fdd,
      damage: item?.returned_damage,
      bc: item?.returned_bc,
      label: returnDateLabel(
        dashboardSettings.filterType,
        item?.report__report_date,
        item?.report__week_number,
        item?.report__report_date__month,
        item?.report__report_date__year,
      ),
    })),
    modalOneName: "Returns To Station Trends",
    modalTwoName: "All Returns To Station",
    modalOneBtn: "View Trends",
    modalTwoBtn: "View All Returns",
  };

  const mockedFleetData: any = {
    tag: "fleet",
    title: "Fleet",
    smallText: `% of active`,
    smallValue: `${data?.fleet.secondary_value}%`,
    alertTheme: data?.fleet.secondary_value > 50 ? "success" : "error",
    total: formatNumber(data?.fleet.total) || 0,
    threeStats: data?.fleet.three_stats.map((stats) => ({
      ...stats,
      count: stats.count || 0,
      list: stats.list.map((item) => ({
        fleetNumber: item.number,
        fleetVendor: item.van_group__name,
        plate: item.plate_number,
        station: item?.station__title,
        reason: outOfServiceReasonConstants[item.status_change_reason]?.option,
      }))
    })),
    period_chart_data: data?.fleet.period_chart_data?.map((item) => ({
      max_fleet_count: item.max_fleet_count,
      max_active_count: item.max_active_count,
      max_inactive_count: item.max_inactive_count,
      max_offboarded_count: item.max_offboarded_count,
      label: returnDateLabel(
        dashboardSettings.filterType,
        item?.date,
        item?.week_number,
        item?.date__month,
        item?.date__year,
      ),
    })),
    modalTwoName: "Fleet Status Trends",
    modalOneBtn: "",
    modalTwoBtn: "View Trends",
  };

  const mockedDaData: any = {
    tag: "da",
    title: "Delivery Associate",
    smallText: `% of active`,
    smallValue: `${data?.da.secondary_value}%`,
    alertTheme: data?.da.secondary_value > 50 ? "success" : "error",
    total: formatNumber(data?.da.total) || 0,
    threeStats: data?.da.three_stats.map((stats) => ({
      ...stats,
      count: stats.count || 0,
      list: stats.list.map((item) => ({
        badge: item.badge,
        fullName: `${item.first_name} ${item.last_name}`,
        transporterID: item?.transporter_id,
        driverLicense: item?.driver_license,
      }))
    })),
    period_chart_data: data?.da.period_chart_data?.map((item) => ({
      max_shift_count: item.max_shift_count,
      max_active_count: item.max_active_count,
      max_inactive_count: item.max_inactive_count,
      max_offboarded_count: item.max_offboarded_count,
      label: returnDateLabel(
        dashboardSettings.filterType,
        item?.date,
        item?.week_number,
        item?.date__month,
        item?.date__year,
      ),
    })),
    modalTwoName: "DA Status Trends",
    modalOneBtn: "",
    modalTwoBtn: "View Trends",
  };

  const mockedRabbitData: any = {
    tag: "rabbit",
    title: "Rabbit",
    smallText: `Total: ${data?.rabbit.total}`,
    smallValue: `${data?.rabbit.third_value}%`,
    alertTheme:
      data?.rabbit.third_value < 100 && data?.rabbit.third_value > 90
        ? "warning"
        : data?.rabbit.third_value < 90
          ? "error"
          : "success",
    total: formatNumber(data?.rabbit.secondary_value),
    threeStats: data?.rabbit.three_stats.map((stats) => ({
      ...stats,
      list: stats.list.map((item) => ({
        date: DateTime.fromISO(item?.report__report_date || "").toFormat("dd-MM-yyyy"),
        station: item?.report__station__title,
        driver: `${item.driver__first_name} ${item.driver__last_name}`,
        serialNumber: item.rabbit_number__serial_number,
      }))
    })),
    modalOneName: "Unreturned Rabbits",
    modalTwoName: "",
    modalOneBtn: "",
    modalTwoBtn: "",
  };

  const mockedDollyData: any = {
    tag: "dolly",
    title: "Dolly",
    smallText: `Total: ${data?.dolly.total}`,
    smallValue: `${data?.dolly.third_value}%`,
    alertTheme:
      data?.dolly.third_value < 100 && data?.dolly.third_value > 90
        ? "warning"
        : data?.dolly.third_value < 90
          ? "error"
          : "success",
    total: formatNumber(data?.dolly.secondary_value),
    threeStats: data?.dolly.three_stats.map((stats) => ({
      ...stats,
      list: stats.list.map((item) => ({
        date: DateTime.fromISO(item?.report__report_date || "").toFormat("dd-MM-yyyy"),
        station: item?.report__station__title,
        driver: `${item.driver__first_name} ${item.driver__last_name}`,
        serialNumber: item.dolly_number__serial_number,
      }))
    })),
    modalOneName: "Unreturned Dolly",
    modalTwoName: "",
    modalOneBtn: "",
    modalTwoBtn: "",
  };

  return {
    routeData: mockedRouteData,
    deliveryData: mockedDeliveryData,
    returnsData: mockedReturnsData,
    rabbitData: mockedRabbitData,
    dollyData: mockedDollyData,
    daData: mockedDaData,
    fleetData: mockedFleetData,
  };
};

const returnDateLabel = (
  type: string | undefined,
  date?: string,
  week_number?: number,
  month?: number,
  year?: number,
) => {
  switch (type) {
    case "date":
      return DateTime.fromISO(date || "").toFormat("dd-MM");
    case "week":
      return `Week ${week_number && week_number + 1}`;
    case "month":
      return month && `${monthShort[month - 1]}, ${year}`;
    case "year":
      return month && monthShort[month - 1];
    default:
      break;
  }
};

const returnRoleCount = (name: string, data: { name: string; count: number }[] | undefined) => {
  switch (name) {
    case "Standard Large":
    case "Standard Cube":
    case "Standard Other":
    case "Flex":
    case "Rejected":
      return data?.find((item) => item.name === name)?.count;
    case "Cancelled":
      return data
        ?.filter((item) => ["MYCU Cancelled", "Client Cancelled"].includes(item.name))
        ?.reduce((a, c) => a + c.count, 0);
    case "Other":
      return data
        ?.filter((item) => ["Rescue", "Stand By", "Training", "Special"].includes(item.name))
        ?.reduce((a, c) => a + c.count, 0);
    default:
      break;
  }
};
